import React from 'react'
import loadable from '@loadable/component'
import {withViewportHydration} from './withViewportHydration'

/* Debug mode toggle */
const isDebugMode = false

/* Pages */
import StandardPage from '../pages/standardPage'
import WebContentPage from '../pages/WebContentPage'
import CartPage from '../pages/cartPage'
import ContainerPage from '../pages/containerPage'
import LandingPage from '../pages/landingPage'

/* Layouts - Heavy components (use Swiper) - loadable with SSR */
// HeroPanelBlock: NOT deferred (above-fold, already has progressive hydration)
const HeroPanelBlock = loadable(() => import('../layouts/heroPanelBlock'), {
    ssr: true,
    fallback: null
})

// Below-fold carousels: loadable with SSR + viewport hydration deferral
// These components accept hydrationCount prop for re-measurement after hydration
const CategoryCarouselBlockBase = loadable(() => import('../layouts/categoryCarouselBlock'), {
    ssr: true,
    fallback: null
})
const CategoryCarouselBlock = withViewportHydration(CategoryCarouselBlockBase, {
    rootMargin: '300px',
    minHeight: 200
})

const CategoryCarouselListBlock = loadable(() => import('../layouts/categoryCarouselListBlock'), {
    ssr: true,
    fallback: null
})

const BigImageAndProductListBlockBase = loadable(
    () => import('../layouts/bigImageAndProductListBlock'),
    {
        ssr: true,
        fallback: null
    }
)
const BigImageAndProductListBlock = withViewportHydration(BigImageAndProductListBlockBase, {
    rootMargin: '300px',
    minHeight: 300
})

/* Layouts - Lightweight components - loadable with SSR */
const WidgetGridBlock = loadable(() => import('../layouts/widgetGridBlock'), {
    ssr: true
})
const ProductSizeGuideBlockLayout = loadable(
    () => import('../layouts/productSizeGuideBlockLayout'),
    {ssr: true}
)
const CategoryBannerBlock = loadable(() => import('../layouts/categoryBannerBlock'), {
    ssr: true
})
const FullPanelBlock = loadable(() => import('../layouts/fullPanelBlock'), {
    ssr: true
})
const WebFooterTopBarBlock = loadable(() => import('../layouts/webFooterTopBarBlock'), {
    ssr: true
})
const TitleDescriptionXHtmlBlock = loadable(() => import('../layouts/TitleDescriptionXHtmlBlock'), {
    ssr: true
})
const TitleDescriptionBlock = loadable(() => import('../layouts/TitleDescriptionBlock'), {
    ssr: true
})
const TitleBlock = loadable(() => import('../layouts/TitleBlock'), {
    ssr: true
})
const CtaItemListBlock = loadable(() => import('../layouts/CtaItemListBlock'), {
    ssr: true
})
const ImageTitleDescriptionBlock = loadable(() => import('../layouts/imageTitleDescriptionBlock'), {
    ssr: true
})
const ErrorPageBlock = loadable(() => import('../layouts/errorPageBlock'), {
    ssr: true
})
const BannerBlock = loadable(() => import('../layouts/bannerBlock'), {
    ssr: true
})
const OrderStatusFormPlaceholderBlock = loadable(
    () => import('../layouts/orderStatusFormPlaceholderBlock'),
    {ssr: true}
)
const PanelCtaBlock = loadable(() => import('../layouts/panelCtaBlock'), {
    ssr: true
})
const LeftRightContentBlock = loadable(() => import('../layouts/LeftRightContentBlock'), {
    ssr: true
})
const SeoFooterStyleBlock = loadable(() => import('../layouts/seoFooterStyleBlock'), {
    ssr: true
})
const ContentWrapperBlock = loadable(() => import('../layouts/contentWrapperBlock'), {
    ssr: true
})
const SpacerBlock = loadable(() => import('../layouts/spacerBlock'), {
    ssr: true
})

/* Assets - Heavy components - loadable with SSR + viewport hydration deferral */
// RecommendationsCarousel accepts hydrationCount prop for re-measurement after hydration
const RecommendationsCarouselBase = loadable(() => import('../assets/recommendationsCarousel'), {
    ssr: true,
    fallback: null
})
const RecommendationsCarousel = withViewportHydration(RecommendationsCarouselBase, {
    rootMargin: '400px',
    minHeight: 300
})

/* Assets - Lightweight components - loadable with SSR */
const CarouselCtaBlock = loadable(() => import('../assets/carouselCtaBlock'), {
    ssr: true
})
const ResponsiveMediaBlock = loadable(() => import('../assets/responsiveMediaBlock'), {
    ssr: true
})
const ImageFile = loadable(() => import('../assets/imageFile'), {
    ssr: true
})
const VideoFile = loadable(() => import('../assets/videoFile'), {
    ssr: true
})
const CtaItemBlock = loadable(() => import('../assets/ctaItemBlock'), {
    ssr: true
})
const WidgetTileBlock = loadable(() => import('../assets/widgetTileBlock'), {
    ssr: true
})
const WebFooterTopBarColumnBlock = loadable(() => import('../assets/webFooterTopBarColumnBlock'), {
    ssr: true
})
const CategoryLinkBlock = loadable(() => import('../assets/categoryLinkBlock'), {
    ssr: true
})
const ProductLinkBlock = loadable(() => import('../assets/productLinkBlock'), {
    ssr: true
})
const MapViewBlock = loadable(() => import('../assets/mapViewBlock'), {
    ssr: true
})
const ResponsiveTextBlock = loadable(() => import('../assets/responsiveTextBlock'), {
    ssr: true
})
const RewardTileBlock = loadable(() => import('../assets/rewardTileBlock'), {
    ssr: true
})
const TileModalBlock = loadable(() => import('../assets/tileModalBlock'), {
    ssr: true
})
const MegaMenuIlustrationBlock = loadable(() => import('../assets/megaMenuIlustrationBlock'), {
    ssr: true
})

/* Assets - Heavy components - loadable with SSR + viewport hydration deferral */
// ProductCarouselBlock fetches products by IDs and renders carousel
const ProductCarouselBlockBase = loadable(() => import('../assets/productCarouselBlock'), {
    ssr: true,
    fallback: null
})
const ProductCarouselBlock = withViewportHydration(ProductCarouselBlockBase, {
    rootMargin: '400px',
    minHeight: 300
})

import {sendClickEvent, setTempRecommendation} from '../../../utils/gtm-utils'

// GTM Modification
/**
 * Sends a recommendation carousel click event to GTM.
 * @param {string} title - Carousel title used for the event type and recommendation name.
 * @param {string} pageType - Page context (e.g. pdp, cart, mini cart).
 * @param {Object} [product] - Clicked product to include as GTM items when present.
 * @returns {void}
 */
const sendGTMEvent = (title, pageType, product) => {
    setTempRecommendation({
        id: 'recommendations',
        name: title
    })
    const carouselName = (title || '').toLowerCase().trim().replace(/\s+/g, '_')
    sendClickEvent(`carousel - ${pageType} - ${carouselName}`, product)
}

export const renderPagesSwitch = (content, area) => {
    if (isDebugMode) {
        console.log('📄 DATA LOADED - PAGE - TYPE: ', content.__typename, 'CONTENT: ', content)
    }
    switch (content.__typename) {
        case 'StandardPage':
            return <StandardPage {...content} />
        case 'CartPage':
            return <CartPage content={content} area={area} />
        case 'WebContentPage': // Homepage
            return <WebContentPage content={content} area={area} />
        case 'ContainerPage':
            return <ContainerPage content={content} />
        case 'LandingPage':
            return <LandingPage {...content} area={area} />
        default:
            return null
    }
}

export const renderComponentsSwitch = (content, props) => {
    if (isDebugMode) {
        console.log(
            '📄 DATA LOADED - COMPONENT - TYPE: ',
            content?.__typename,
            'CONTENT: ',
            content,
            'PROPS:',
            props
        )
    }
    switch (content?.__typename) {
        case 'CarouselCtaBlock':
            return <CarouselCtaBlock {...content} {...props} />
        case 'ResponsiveMediaBlock':
            return <ResponsiveMediaBlock {...content} {...props} />
        case 'ImageFile':
            return <ImageFile {...content} {...props} />
        case 'VideoFile':
            return <VideoFile {...content} {...props} />
        case 'CtaItemBlock':
            return <CtaItemBlock {...content} {...props} />
        case 'WidgetTileBlock':
            return <WidgetTileBlock {...content} {...props} />
        case 'WebFooterTopBarColumnBlock':
            return <WebFooterTopBarColumnBlock {...content} {...props} />
        case 'ProductSizeGuideBlock':
            return <ProductSizeGuideBlockLayout {...content} {...props} />
        case 'CategoryLinkBlock':
            return <CategoryLinkBlock {...content} {...props} />
        case 'ProductLinkBlock':
            return <ProductLinkBlock {...content} {...props} />
        case 'MapViewBlock':
            return <MapViewBlock {...content} {...props} />
        case 'ResponsiveTextBlock':
            return <ResponsiveTextBlock {...content} {...props} />
        case 'RewardTileBlock':
            return <RewardTileBlock {...content} {...props} />
        case 'TileModalBlock':
            return <TileModalBlock {...content} {...props} />
        case 'MegaMenuIlustrationBlock':
            return <MegaMenuIlustrationBlock {...content} {...props} />
        default:
            return null
    }
}

export const renderLayoutsSwitch = (content, props) => {
    if (isDebugMode) {
        console.log('📄 DATA LOADED - LAYOUT - TYPE: ', content?.__typename, 'CONTENT: ', content)
    }

    switch (content?.__typename) {
        case 'HeroPanelBlock':
            return <HeroPanelBlock {...content} />
        case 'PanelCtaBlock':
            return <PanelCtaBlock {...content} {...props} />
        case 'CategoryBannerBlock':
            return <CategoryBannerBlock {...content} />
        case 'WidgetGridBlock':
            return <WidgetGridBlock {...content} />
        case 'ProductSizeGuideBlock':
            return <ProductSizeGuideBlockLayout content={content} />
        case 'FullPanelBlock':
            return <FullPanelBlock {...content} />
        case 'WebFooterTopBarBlock':
            return <WebFooterTopBarBlock {...content} />
        case 'TitleDescriptionXHtmlBlock':
            return <TitleDescriptionXHtmlBlock {...content} {...props} />
        case 'CtaItemListBlock':
            return <CtaItemListBlock {...content} {...props} />
        case 'TitleBlock':
            return <TitleBlock {...content} />
        case 'CategoryCarouselBlock':
            return <CategoryCarouselBlock {...content} {...props} />
        case 'CategoryCarouselListBlock':
            return <CategoryCarouselListBlock {...content} {...props} />
        case 'ImageTitleDescriptionBlock':
            return <ImageTitleDescriptionBlock {...content} {...props} />
        case 'TitleDescriptionBlock':
            return <TitleDescriptionBlock {...content} {...props} />
        case 'BigImageAndProductListBlock':
            return <BigImageAndProductListBlock {...content} {...props} />
        case 'ErrorPageBlock':
            return <ErrorPageBlock {...content} {...props} />
        case 'BannerBlock':
            return <BannerBlock {...content} {...props} />
        case 'OrderStatusFormPlaceholderBlock':
            return <OrderStatusFormPlaceholderBlock {...content} {...props} />
        case 'LeftRightContentBlock':
            return <LeftRightContentBlock {...content} {...props} />
        case 'ProductLinkBlock':
            return <ProductLinkBlock {...content} {...props} isLayout={true} />
        case 'SeoFooterStyleBlock':
            return <SeoFooterStyleBlock {...content} {...props} />
        case 'ContentWrapperBlock':
            return <ContentWrapperBlock {...content} {...props} />
        case 'SpacerBlock':
            return <SpacerBlock {...content} {...props} />
        case 'ProductCarouselBlock':
            return <ProductCarouselBlock {...content} {...props} />
        case 'VickyProductCarouselBlock':
            return (
                <RecommendationsCarousel
                    {...content}
                    {...props}
                    onProductClick={(product) =>
                        sendGTMEvent(content.CarouselTitle, props?.pageType, product)
                    }
                />
            )
        default:
            return null
    }
}

export const renderCarouselItems = (content, carouselID, productSku = null, isMiniCart = false) => {
    const pageType = carouselID?.startsWith('pdp-') ? 'pdp' : isMiniCart ? 'mini cart' : 'cart'
    switch (content.__typename) {
        case 'VickyProductCarouselBlock':
            // Each carousel fetches its own VickyAI data based on AIModel
            // productSku is required for 'similars' and 'bought-together' AIModels
            return (
                <RecommendationsCarousel
                    {...content}
                    productSku={productSku}
                    carouselID={carouselID}
                    onProductClick={(product) =>
                        sendGTMEvent(content.CarouselTitle, pageType, product)
                    }
                    isMiniCart={isMiniCart}
                />
            )
        default:
            return null
    }
}
